"use client";

import { truncateAddress } from "@aptos-labs/ts-sdk";
import { IconDeviceMobile } from "@decibeltrade/react-icons";
import { ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useRef } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { getSdkConfig } from "@/contexts/dex-sdk";
import { parseSharedKey, useImportSessionKey } from "@/hooks/use-imported-session-keys";
import { addressesEqual } from "@/utils/address";

import { toast } from "../toast";
import { ScanQrOverlay } from "../vectors/scan-qr-overlay";
import { ModalCloseButton } from "./modal-close-button";

const Scanner = dynamic(() => import("@yudiel/react-qr-scanner").then((mod) => mod.Scanner), {
  ssr: false,
});

export const openScanQrCodeModal = ({ onSuccess }: { onSuccess?: () => void }) => {
  openModal({
    id: "scan-qr-code",
    renderContent: (modalProps) => <ScanQrCodeModal {...modalProps} onSuccess={onSuccess} />,
  });
};

export const ScanQrCodeModal = ({
  close,
  onSuccess,
}: ModalRenderProps & { onSuccess?: () => void }) => {
  const t = useTranslations("scanQrCodeModal");
  const tGeneral = useTranslations("general");
  const importSessionKey = useImportSessionKey();

  const status = useRef<"idle" | "importing" | "success" | "error">("idle");

  return (
    <div className={modalContentStyles}>
      <ModalCloseButton close={close} />
      <div className={headerStyles}>
        <h2 className={titleStyles}>{t("title")}</h2>
        <div className={subtitleStyles}>
          {t("subtitle1")}
          <IconDeviceMobile
            className={css({
              display: "inline",
              h: "[18px]",
              w: "[18px]",
            })}
          />{" "}
          {t("subtitle2")}
        </div>
      </div>

      <div className={contentStyles}>
        <Scanner
          // onScan can fire multiple times when user is scanning
          onScan={(qrCodes) => {
            if (status.current !== "idle") return;
            try {
              status.current = "importing";
              qrCodes.forEach((qrCode) => {
                const result = parseSharedKey(qrCode.rawValue);

                if (!result) throw new Error("Invalid shared key");

                const { sharedKey, deploymentPackage } = result;

                // Match hosts to make sure that the key is on same chain
                if (!addressesEqual(deploymentPackage, getSdkConfig().deployment.package)) {
                  toast({
                    variant: "error",
                    title: tGeneral("error"),
                    description: t("toast.errorInvalidChain"),
                  });
                  return;
                }

                importSessionKey(sharedKey);
                close();
                onSuccess?.();
                toast({
                  variant: "success",
                  title: tGeneral("success"),
                  description: t("toast.success", {
                    walletName: sharedKey.label ?? truncateAddress(sharedKey.subaccountAddress),
                  }),
                });
                status.current = "success";
              });
            } catch {
              status.current = "error";
              toast({
                variant: "error",
                title: tGeneral("error"),
                description: t("toast.errorFailedToConnect"),
              });
            }
          }}
          onError={() => {
            status.current = "error";
            toast({
              variant: "error",
              title: tGeneral("error"),
              description: t("toast.errorFailedToConnect"),
            });
          }}
          sound={false}
          allowMultiple={false}
          constraints={{
            facingMode: "environment",
          }}
          classNames={{ container: cameraContainerStyles }}
          styles={{ container: { width: "100%" } }}
          components={{ finder: false, torch: false, onOff: false, zoom: false }}
        />
        <ScanQrOverlay className={css({ position: "absolute", h: "[70vw]", w: "[70vw]" })} />
      </div>
    </div>
  );
};

const modalContentStyles = stack({
  bg: "background.primary",
  h: "[100dvh]",
  w: "[100dvw]",
  gap: "16",
  p: "16",
});

const headerStyles = stack({
  gap: "20",
  mt: "64",
  mx: "24",
  textAlign: "center",
});

const titleStyles = css({
  textStyle: "highlight.xl",
  color: "text.primary",
});

const subtitleStyles = css({
  textStyle: "body.lg",
  color: "text.tertiary",
});

const contentStyles = flex({
  flex: "1",
  align: "center",
  justify: "center",
  gap: "16",
  position: "relative",
});

const cameraContainerStyles = css({
  borderWidth: "100",
  borderColor: "border.primary",
  borderRadius: "300",
});
